import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Save, Play, Pause, Square, Trophy, Users, Target, CheckCircle, Plus, Minus, Award, Edit3 } from 'lucide-react';
import { doc, getDoc, updateDoc, collection, addDoc, onSnapshot, query, where, getDocs, runTransaction } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';

const LiveScoringPage = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { isSuperAdmin, isScoreKeeper, userRole } = useAuth();
  const [match, setMatch] = useState(null);
  const [league, setLeague] = useState(null);
  const [teams, setTeams] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Match state
  const [matchStatus, setMatchStatus] = useState('scheduled');
  const [currentSet, setCurrentSet] = useState(1);
  const [deuceCount, setDeuceCount] = useState(0);
  const [savingPlayerStats, setSavingPlayerStats] = useState(false);
  const [isUpdatingPlayerStats, setIsUpdatingPlayerStats] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [isManuallySettingCurrentSet, setIsManuallySettingCurrentSet] = useState(false);

  // Helper function to get players in consistent order
  const getPlayersInOrder = (teamStats) => {
    if (!teamStats) return [];
    const players = Object.entries(teamStats)
      .sort(([a], [b]) => a.localeCompare(b)) // Sort by player key (player1, player2)
      .map(([playerKey, stats]) => ({ playerKey, stats }));
    
    console.log('Player order:', players.map(p => p.playerKey));
    return players;
  };
  const [sets, setSets] = useState([
    { 
      set: 1, 
      homeScore: 0, 
      awayScore: 0, 
      homeGames: 0, 
      awayGames: 0, 
      homePoints: 0, 
      awayPoints: 0,
      completed: false,
      inTiebreak: false
    },
    { 
      set: 2, 
      homeScore: 0, 
      awayScore: 0, 
      homeGames: 0, 
      awayGames: 0, 
      homePoints: 0, 
      awayPoints: 0,
      completed: false,
      inTiebreak: false
    },
    { 
      set: 3, 
      homeScore: 0, 
      awayScore: 0, 
      homeGames: 0, 
      awayGames: 0, 
      homePoints: 0, 
      awayPoints: 0,
      completed: false,
      isSuperTiebreak: true
    }
  ]);

  // Player stats for each set
  const [playerStats, setPlayerStats] = useState({
    home: {
      player1: { aces: 0, unforcedErrors: 0, kickSmashes: 0, sportsmanshipRating: 5 },
      player2: { aces: 0, unforcedErrors: 0, kickSmashes: 0, sportsmanshipRating: 5 }
    },
    away: {
      player1: { aces: 0, unforcedErrors: 0, kickSmashes: 0, sportsmanshipRating: 5 },
      player2: { aces: 0, unforcedErrors: 0, kickSmashes: 0, sportsmanshipRating: 5 }
    }
  });

  useEffect(() => {
    let unsubscribe = null;

    const setupRealTimeListener = () => {
      console.log('🔄 Setting up real-time listener for match:', matchId);
      
      unsubscribe = onSnapshot(doc(db, 'fixtures', matchId), async (matchDoc) => {
        if (matchDoc.exists()) {
          const matchData = { id: matchDoc.id, ...matchDoc.data() };
          console.log('📡 Real-time match data received:', {
            status: matchData.status,
            currentSet: matchData.currentSet,
            playerStats: matchData.playerStats ? 'present' : 'missing',
            sets: matchData.sets ? matchData.sets.length : 0
          });
          
          setMatch(matchData);
          setMatchStatus(matchData.status || 'scheduled');
          setLastUpdateTime(new Date().toLocaleTimeString());
          
          // Load existing match data if available
          if (matchData.sets) {
            const sanitizedSets = matchData.sets.map(set => ({
              ...set,
              homePoints: isNaN(set.homePoints) ? 0 : set.homePoints,
              awayPoints: isNaN(set.awayPoints) ? 0 : set.awayPoints,
              homeGames: isNaN(set.homeGames) ? 0 : set.homeGames,
              awayGames: isNaN(set.awayGames) ? 0 : set.awayGames,
              homeScore: isNaN(set.homeScore) ? 0 : set.homeScore,
              awayScore: isNaN(set.awayScore) ? 0 : set.awayScore
            }));
            setSets(sanitizedSets);
          }
          if (matchData.playerStats && !isUpdatingPlayerStats) {
            console.log('📊 Updating player stats from real-time data');
            setPlayerStats(matchData.playerStats);
          } else if (!matchData.playerStats && !isUpdatingPlayerStats) {
            // Ensure playerStats has proper structure even if not in database
            const defaultPlayerStats = {
              home: {
                player1: { aces: 0, unforcedErrors: 0, kickSmashes: 0, sportsmanshipRating: 5 },
                player2: { aces: 0, unforcedErrors: 0, kickSmashes: 0, sportsmanshipRating: 5 }
              },
              away: {
                player1: { aces: 0, unforcedErrors: 0, kickSmashes: 0, sportsmanshipRating: 5 },
                player2: { aces: 0, unforcedErrors: 0, kickSmashes: 0, sportsmanshipRating: 5 }
              }
            };
            setPlayerStats(defaultPlayerStats);
          }
          if (matchData.currentSet && !isManuallySettingCurrentSet) {
            console.log(`📡 Real-time listener updating currentSet from ${currentSet} to ${matchData.currentSet}`);
            setCurrentSet(matchData.currentSet);
          } else if (matchData.currentSet && isManuallySettingCurrentSet) {
            console.log(`🚫 Real-time listener blocked - manual set selection in progress`);
          }
          if (matchData.deuceCount) {
            setDeuceCount(matchData.deuceCount);
          }

          // Fetch team data for logos
          if (matchData.homeTeamId && matchData.awayTeamId) {
            try {
              const [homeTeamDoc, awayTeamDoc] = await Promise.all([
                getDoc(doc(db, 'teams', matchData.homeTeamId)),
                getDoc(doc(db, 'teams', matchData.awayTeamId))
              ]);
              
              const teamData = {};
              if (homeTeamDoc.exists()) {
                teamData[matchData.homeTeamId] = { id: homeTeamDoc.id, ...homeTeamDoc.data() };
              }
              if (awayTeamDoc.exists()) {
                teamData[matchData.awayTeamId] = { id: awayTeamDoc.id, ...awayTeamDoc.data() };
              }
              setTeams(teamData);
            } catch (err) {
              console.error('Error fetching team data:', err);
            }
          }

          // Fetch league details
          if (matchData.leagueId) {
            const leagueDoc = await getDoc(doc(db, 'leagues', matchData.leagueId));
            if (leagueDoc.exists()) {
              setLeague({ id: leagueDoc.id, ...leagueDoc.data() });
            }
          }
          setLoading(false);
        } else {
          setError('Match not found');
          setLoading(false);
        }
      }, (error) => {
        console.error('❌ Real-time listener error:', error);
        setError('Failed to load match data');
        setLoading(false);
      });
    };

    setupRealTimeListener();

    return () => {
      if (unsubscribe) {
        console.log('🧹 Cleaning up real-time listener');
        unsubscribe();
      }
    };
  }, [matchId]);

  const updatePlayerStat = async (team, player, stat, increment) => {
    // Ensure playerStats structure exists
    if (!playerStats || !playerStats[team] || !playerStats[team][player]) {
      console.warn('Player stats structure not properly initialized');
      return;
    }
    
    // Update local state immediately for responsive UI
    const newPlayerStats = {
      ...playerStats,
      [team]: {
        ...playerStats[team],
        [player]: {
          ...playerStats[team][player],
          [stat]: Math.max(0, (playerStats[team][player][stat] || 0) + increment)
        }
      }
    };
    
    setPlayerStats(newPlayerStats);
    setSavingPlayerStats(true);
    setIsUpdatingPlayerStats(true);
    
    // Use atomic operation to update only player stats
    try {
      await runTransaction(db, async (transaction) => {
        const matchRef = doc(db, 'fixtures', matchId);
        const matchDoc = await transaction.get(matchRef);
        
        if (!matchDoc.exists()) {
          throw new Error('Match document does not exist');
        }
        
        const currentData = matchDoc.data();
        const currentPlayerStats = currentData.playerStats || {};
        
        // Merge player stats atomically
        const updatedPlayerStats = {
          ...currentPlayerStats,
          [team]: {
            ...currentPlayerStats[team],
            [player]: {
              ...currentPlayerStats[team]?.[player],
              [stat]: Math.max(0, (currentPlayerStats[team]?.[player]?.[stat] || 0) + increment)
            }
          }
        };
        
        transaction.update(matchRef, {
          playerStats: updatedPlayerStats,
          updatedAt: new Date().toISOString()
        });
      });
      
      console.log(`✅ Player stat updated: ${team} ${player} ${stat} ${increment > 0 ? '+' : ''}${increment}`);
    } catch (error) {
      console.error('Error updating player stat:', error);
      // Revert local state on error
      setPlayerStats(playerStats);
    } finally {
      setSavingPlayerStats(false);
      setIsUpdatingPlayerStats(false);
    }
  };

  // Admin function to directly update set scores (Super Admin only)
  const updateSetScoreAdmin = async (setIndex, homeGames, awayGames) => {
    if (!isSuperAdmin()) {
      console.error('Unauthorized: Only super admins can edit set scores');
      return;
    }

    const newSets = [...sets];
    const set = newSets[setIndex];
    
    set.homeGames = Math.max(0, homeGames);
    set.awayGames = Math.max(0, awayGames);
    
    const isSuperTiebreak = setIndex === 2;
    
    if (isSetComplete(set.homeGames, set.awayGames, isSuperTiebreak)) {
      set.completed = true;
      set.homeScore = set.homeGames;
      set.awayScore = set.awayGames;
      
      // Set completed - let user manually select next set
      console.log(`🎾 Set ${setIndex + 1} completed via admin!`);
      
      // Show visual feedback for set completion
      setTimeout(() => {
        alert(`🎾 Set ${setIndex + 1} completed! Please select the next set manually.`);
      }, 100);
    } else {
      set.completed = false;
      set.homeScore = set.homeGames;
      set.awayScore = set.awayGames;
    }
    
    setSets(newSets);
    await saveMatchData();
  };

  // Regular function to update set scores (for all users)
  const updateSetScore = async (setIndex, homeGames, awayGames) => {
    const newSets = [...sets];
    const set = newSets[setIndex];
    
    set.homeGames = Math.max(0, homeGames);
    set.awayGames = Math.max(0, awayGames);
    
    const isSuperTiebreak = setIndex === 2;
    
    if (isSetComplete(set.homeGames, set.awayGames, isSuperTiebreak)) {
      set.completed = true;
      set.homeScore = set.homeGames;
      set.awayScore = set.awayGames;
      
      // Set completed - let user manually select next set
      console.log(`🎾 Set ${setIndex + 1} completed via game controls!`);
      
      // Show visual feedback for set completion
      setTimeout(() => {
        alert(`🎾 Set ${setIndex + 1} completed! Please select the next set manually.`);
      }, 100);
    } else {
      set.completed = false;
      set.homeScore = set.homeGames;
      set.awayScore = set.awayGames;
    }
    
    setSets(newSets);
    await saveMatchData();
  };

  // Tennis scoring logic
  const getTennisScore = (points) => {
    if (points === 0) return '0';
    if (points === 1) return '15';
    if (points === 2) return '30';
    if (points === 3) return '40';
    return points.toString();
  };

  const getGameScore = (homePoints, awayPoints) => {
    if (homePoints >= 3 && awayPoints >= 3) {
      const diff = homePoints - awayPoints;
      if (diff === 0) {
        if (homePoints >= 4 && awayPoints >= 4) {
          return 'Golden Point';
        }
        return 'Deuce';
      }
      if (diff === 1) return `Adv ${match?.homeTeam || 'Home'}`;
      if (diff === -1) return `Adv ${match?.awayTeam || 'Away'}`;
      if (diff >= 2) return `Game ${match?.homeTeam || 'Home'}`;
      if (diff <= -2) return `Game ${match?.awayTeam || 'Away'}`;
    }
    
    if (homePoints >= 4 && awayPoints < 3) return `Game ${match?.homeTeam || 'Home'}`;
    if (awayPoints >= 4 && homePoints < 3) return `Game ${match?.awayTeam || 'Away'}`;
    
    return `${getTennisScore(homePoints)} - ${getTennisScore(awayPoints)}`;
  };

  const isSetComplete = (homeGames, awayGames, isSuperTiebreak = false) => {
    if (isSuperTiebreak) {
      if (homeGames >= 10 && awayGames >= 10) {
        return Math.abs(homeGames - awayGames) >= 2;
      }
      return homeGames >= 10 || awayGames >= 10;
    }
    
    if (homeGames >= 6 && awayGames >= 6) {
      if (homeGames >= 7 && awayGames >= 7) {
        return Math.abs(homeGames - awayGames) >= 2;
      }
      return homeGames >= 7 || awayGames >= 7;
    }
    
    if (homeGames >= 6 || awayGames >= 6) {
      return Math.abs(homeGames - awayGames) >= 2;
    }
    
    return false;
  };

  const updateTennisScore = async (team, direction = 1) => {
    let activeSetIndex = sets.findIndex(s => !s.completed);
    
    if (activeSetIndex === -1) {
      activeSetIndex = currentSet - 1;
    }
    
    const activeSetData = sets[activeSetIndex];
    
    if (!activeSetData || activeSetData.completed) {
      return;
    }

    const newSets = [...sets];
    const setData = newSets[activeSetIndex];
    const isSuperTiebreak = (activeSetIndex + 1) === 3;
    const inTiebreak = setData.inTiebreak || false;
    
    // For super tiebreak (Set 3), directly update the games score instead of points
    if (isSuperTiebreak) {
      if (team === 'home') {
        const currentGames = isNaN(setData.homeGames) ? 0 : setData.homeGames;
        setData.homeGames = Math.max(0, currentGames + direction);
      } else {
        const currentGames = isNaN(setData.awayGames) ? 0 : setData.awayGames;
        setData.awayGames = Math.max(0, currentGames + direction);
      }
      
      // Check if super tiebreak is complete (first to 10, win by 2)
      const homeGames = setData.homeGames;
      const awayGames = setData.awayGames;
      
      if ((homeGames >= 10 || awayGames >= 10) && 
          Math.abs(homeGames - awayGames) >= 2) {
        setData.completed = true;
        setData.homeScore = homeGames;
        setData.awayScore = awayGames;
        
        console.log(`Super tiebreak completed! Final score: ${homeGames}-${awayGames}`);
      }
      
      setSets(newSets);
      await saveMatchData();
      return;
    }
    
    // Regular tennis scoring for Sets 1 and 2
    if (team === 'home') {
      const currentPoints = isNaN(setData.homePoints) ? 0 : setData.homePoints;
      setData.homePoints = Math.max(0, currentPoints + direction);
    } else {
      const currentPoints = isNaN(setData.awayPoints) ? 0 : setData.awayPoints;
      setData.awayPoints = Math.max(0, currentPoints + direction);
    }

    const homePoints = setData.homePoints;
    const awayPoints = setData.awayPoints;

    if (homePoints === 3 && awayPoints === 3) {
      const currentDeuceCount = deuceCount + 1;
      setDeuceCount(currentDeuceCount);
    }

    if (direction > 0) {
      let gameWon = false;
      let gameWinner = null;

      if (inTiebreak) {
        const winningScore = 7;
        
        if ((homePoints >= winningScore || awayPoints >= winningScore) && 
            Math.abs(homePoints - awayPoints) >= 2) {
          gameWon = true;
          gameWinner = homePoints > awayPoints ? 'home' : 'away';
        }
      } else {
        const isGoldenPoint = deuceCount >= 2;

        if (isGoldenPoint && (homePoints > 3 || awayPoints > 3)) {
          gameWon = true;
          gameWinner = homePoints > awayPoints ? 'home' : 'away';
        } else if (homePoints >= 4 || awayPoints >= 4) {
          if (Math.abs(homePoints - awayPoints) >= 2) {
            gameWon = true;
            gameWinner = homePoints > awayPoints ? 'home' : 'away';
          }
        }
      }

      if (gameWon && gameWinner) {
        if (gameWinner === 'home') {
          setData.homeGames += 1;
        } else {
          setData.awayGames += 1;
        }
        
        setData.homePoints = 0;
        setData.awayPoints = 0;
        setDeuceCount(0);
        
        let setComplete = false;
        
        if (inTiebreak) {
          if ((setData.homeGames >= 7 || setData.awayGames >= 7) && 
              Math.abs(setData.homeGames - setData.awayGames) >= 2) {
            setComplete = true;
          }
        } else {
          if (setData.homeGames === 6 && setData.awayGames === 6) {
            setData.inTiebreak = true;
          } else if ((setData.homeGames >= 6 || setData.awayGames >= 6) && 
                     Math.abs(setData.homeGames - setData.awayGames) >= 2) {
            setComplete = true;
          }
        }
        
        if (setComplete) {
          setData.completed = true;
          setData.homeScore = setData.homeGames;
          setData.awayScore = setData.awayGames;
          
          setSets(newSets);
          
          // Set completed - let user manually select next set
          console.log(`🎾 Set ${activeSetIndex + 1} completed!`);
          
          // Show visual feedback for set completion
          setTimeout(() => {
            alert(`🎾 Set ${activeSetIndex + 1} completed! Please select the next set manually.`);
          }, 100);
          
          // Save both the set completion and current set change
          await saveMatchData();
          return;
        }
      }
    }
    
    setSets(newSets);
    await saveMatchData();
  };

  const startMatch = async () => {
    setMatchStatus('live');
    await saveMatchData();
  };

  const pauseMatch = async () => {
    setMatchStatus('paused');
    await saveMatchData();
  };

  const updateTeamStandings = async (homeSetsWon, awaySetsWon, homePoints, awayPoints) => {
    try {
      const teamsQuery = query(
        collection(db, 'teams'),
        where('name', 'in', [match?.homeTeam, match?.awayTeam])
      );
      
      const teamsSnapshot = await getDocs(teamsQuery);
      const teamsData = {};
      
      teamsSnapshot.docs.forEach(doc => {
        teamsData[doc.data().name] = { id: doc.id, ...doc.data() };
      });
      
      const homeTeam = teamsData[match?.homeTeam];
      const awayTeam = teamsData[match?.awayTeam];
      
      if (!homeTeam || !awayTeam) {
        return;
      }
      
      const homeWon = homeSetsWon >= 2;
      const awayWon = awaySetsWon >= 2;
      
      const homeUpdate = {
        matchesPlayed: (homeTeam.matchesPlayed || 0) + 1,
        points: (homeTeam.points || 0) + homePoints,
        wins: homeWon ? (homeTeam.wins || 0) + 1 : (homeTeam.wins || 0),
        losses: awayWon ? (homeTeam.losses || 0) + 1 : (homeTeam.losses || 0),
        setsFor: (homeTeam.setsFor || 0) + homeSetsWon,
        setsAgainst: (homeTeam.setsAgainst || 0) + awaySetsWon,
        updatedAt: new Date().toISOString()
      };
      
      const awayUpdate = {
        matchesPlayed: (awayTeam.matchesPlayed || 0) + 1,
        points: (awayTeam.points || 0) + awayPoints,
        wins: awayWon ? (awayTeam.wins || 0) + 1 : (awayTeam.wins || 0),
        losses: homeWon ? (awayTeam.losses || 0) + 1 : (awayTeam.losses || 0),
        setsFor: (awayTeam.setsFor || 0) + awaySetsWon,
        setsAgainst: (awayTeam.setsAgainst || 0) + homeSetsWon,
        updatedAt: new Date().toISOString()
      };
      
      await updateDoc(doc(db, 'teams', homeTeam.id), homeUpdate);
      await updateDoc(doc(db, 'teams', awayTeam.id), awayUpdate);
      
    } catch (error) {
      console.error('Error updating team standings:', error);
    }
  };

  const endMatch = async () => {
    const homeSetsWon = sets.filter(s => s.completed && s.homeScore > s.awayScore).length;
    const awaySetsWon = sets.filter(s => s.completed && s.awayScore > s.homeScore).length;
    
    let homePoints = 0;
    let awayPoints = 0;
    
    homePoints = homeSetsWon;
    awayPoints = awaySetsWon;
    
    if (homeSetsWon >= 2) {
      homePoints += 1;
    } else if (awaySetsWon >= 2) {
      awayPoints += 1;
    }
    
    await updateTeamStandings(homeSetsWon, awaySetsWon, homePoints, awayPoints);
    
    setMatchStatus('completed');
    
    await saveMatchData({ 
      status: 'completed',
      homeSetsWon, 
      awaySetsWon, 
      homePoints, 
      awayPoints,
      finalScore: `${homeSetsWon}-${awaySetsWon}`,
      winner: homeSetsWon >= 2 ? match?.homeTeam : match?.awayTeam,
      completedAt: new Date().toISOString()
    });
  };

  const saveMatchData = async (additionalData = {}, overrideCurrentSet = null) => {
    try {
      // Use Firestore transaction to prevent race conditions
      await runTransaction(db, async (transaction) => {
        const matchRef = doc(db, 'fixtures', matchId);
        const matchDoc = await transaction.get(matchRef);
        
        if (!matchDoc.exists()) {
          throw new Error('Match document does not exist');
        }
        
        const currentData = matchDoc.data();
        
        // Merge the new data with existing data to prevent overwrites
      const matchData = {
          ...currentData, // Keep existing data
        status: matchStatus,
        currentSet: overrideCurrentSet !== null ? overrideCurrentSet : currentSet,
        sets,
          playerStats: {
            ...currentData.playerStats, // Keep existing player stats
            ...playerStats // Merge in new player stats
          },
          deuceCount,
        updatedAt: new Date().toISOString(),
          ...additionalData
        };

        console.log(`💾 Saving to Firestore - currentSet: ${overrideCurrentSet !== null ? overrideCurrentSet : currentSet}, status: ${matchStatus}`);
        transaction.update(matchRef, matchData);
      });
      
      if (additionalData.status === 'completed') {
        // Get the final match data for completion
        const finalMatchDoc = await getDoc(doc(db, 'fixtures', matchId));
        const finalMatchData = finalMatchDoc.data();
        
        const cleanMatchData = Object.fromEntries(
          Object.entries(finalMatchData).filter(([_, value]) => value !== undefined)
        );
        
        await addDoc(collection(db, 'matches'), {
          fixtureId: matchId,
          leagueId: match?.leagueId || null,
          homeTeam: match?.homeTeam || null,
          awayTeam: match?.awayTeam || null,
          ...cleanMatchData,
          completedAt: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error saving match data:', error);
    }
  };

  const getCurrentScore = () => {
    const homeSetsWon = sets.filter(s => s.completed && s.homeScore > s.awayScore).length;
    const awaySetsWon = sets.filter(s => s.completed && s.awayScore > s.homeScore).length;
    return `${homeSetsWon}-${awaySetsWon}`;
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white' }}>
        <div style={{ textAlign: 'center' }}>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          >
            <Trophy size={48} style={{ margin: '0 auto 1rem', color: '#10B981' }} />
          </motion.div>
          <p style={{ color: '#6B7280', fontWeight: '600' }}>Loading match...</p>
        </div>
      </div>
    );
  }

  if (error && !match) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white' }}>
        <div style={{ textAlign: 'center', padding: '3rem 0' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#DC2626' }}>
            {error}
          </h2>
          <button
            onClick={() => navigate('/fixtures')}
            style={{
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '0.75rem',
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Back to Fixtures
          </button>
        </div>
      </div>
    );
  }

  const currentSetData = sets[currentSet - 1];

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'white',
      padding: '0.25rem',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      <div style={{ 
        maxWidth: '100%', 
        margin: '0 auto',
        width: '100%',
        display: 'grid',
        gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr',
        gap: '0.5rem',
        height: window.innerWidth < 768 ? 'auto' : '100vh',
        overflow: 'hidden'
      }}>
        {/* LEFT COLUMN - Main Scoring Controls */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', overflow: 'hidden' }}>
          {/* Compact Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.25rem 0'
            }}
        >
          <button
            onClick={() => navigate('/fixtures')}
            style={{
              display: 'flex',
              alignItems: 'center',
                gap: '0.25rem',
                color: '#6B7280',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
                fontSize: '0.75rem',
              fontWeight: '600'
            }}
          >
              <ArrowLeft size={14} />
              Back
          </button>
            
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              {matchStatus === 'scheduled' && (
                <button onClick={startMatch} style={{
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.125rem'
                }}>
                  <Play size={12} />
                  Start
                </button>
              )}
              {matchStatus === 'live' && (
                <button onClick={pauseMatch} style={{
                  background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.125rem'
                }}>
                  <Pause size={12} />
                    Pause
                  </button>
              )}
              {matchStatus === 'paused' && (
                <button onClick={startMatch} style={{
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.125rem'
                }}>
                  <Play size={12} />
                  Resume
                </button>
              )}
          </div>
        </motion.div>

          {/* Match Header - Team Logos & Score */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)',
              borderRadius: '0.5rem',
              padding: '0.5rem',
              border: '2px solid #E2E8F0'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              {/* Home Team */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                {teams[match?.homeTeamId]?.logo && (
                  <img 
                    src={teams[match?.homeTeamId]?.logo} 
                    alt={match?.homeTeam}
                    style={{ 
                      width: '32px', 
                      height: '32px', 
                      objectFit: 'cover',
                      borderRadius: '0.375rem',
                      border: '2px solid #10B981'
                    }}
                  />
                )}
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#1C563D' }}>
                    {match?.homeTeam}
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#10B981' }}>
                  {currentSetData?.homeGames || 0}
                </div>
                </div>
              </div>

              {/* VS */}
              <div style={{ 
                fontSize: '1rem', 
                fontWeight: 'bold', 
                color: '#6B7280',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                vs
              </div>

              {/* Away Team */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, flexDirection: 'row-reverse' }}>
                {teams[match?.awayTeamId]?.logo && (
                  <img 
                    src={teams[match?.awayTeamId]?.logo} 
                    alt={match?.awayTeam}
                    style={{ 
                      width: '32px', 
                      height: '32px', 
                      objectFit: 'cover',
                      borderRadius: '0.375rem',
                      border: '2px solid #3B82F6'
                    }}
                  />
                )}
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#1E40AF' }}>
                    {match?.awayTeam}
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#3B82F6' }}>
                  {currentSetData?.awayGames || 0}
                </div>
                </div>
              </div>
            </div>

            {/* Match Score */}
            <div style={{
              textAlign: 'center', 
              marginTop: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: 'bold',
              color: '#374151'
            }}>
              Match: {getCurrentScore()}
            </div>
          </motion.div>

          {/* Set Scores Row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '0.25rem'
            }}
          >
            {sets.map((set, index) => (
              <div
                key={set.set}
                style={{
                  padding: '0.5rem',
                  borderRadius: '0.5rem',
                  background: set.completed ? 'linear-gradient(135deg, #D1FAE5 0%, #6EE7B7 100%)' :
                              index === currentSet - 1 ? 'linear-gradient(135deg, #DBEAFE 0%, #93C5FD 100%)' :
                              'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)',
                  border: index === currentSet - 1 ? '2px solid #3B82F6' : '1px solid #E5E7EB',
                  textAlign: 'center'
                }}
              >
                <div style={{ fontSize: '0.625rem', fontWeight: '700', color: '#6B7280', marginBottom: '0.125rem' }}>
                  {set.set === 3 ? 'SUPER' : `SET ${set.set}`}
                </div>
                <div style={{ fontSize: '1rem', fontWeight: 'bold', color: set.completed ? '#059669' : index === currentSet - 1 ? '#2563EB' : '#6B7280' }}>
                  {set.homeGames || 0} - {set.awayGames || 0}
                </div>
                {set.completed && (
                  <CheckCircle size={12} style={{ margin: '0.125rem auto 0', color: '#059669' }} />
                )}
              </div>
            ))}
          </motion.div>

            {/* Super Admin Set Score Editors */}
            {isSuperAdmin() && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                padding: '0.5rem',
                  backgroundColor: '#FEF3C7',
                borderRadius: '0.5rem',
                  border: '2px solid #F59E0B'
                }}
              >
                <div style={{ 
                fontSize: '0.625rem', 
                fontWeight: '700', 
                    color: '#92400E',
                marginBottom: '0.25rem',
                textAlign: 'center'
                  }}>
                    Super Admin: Edit Set Scores
                </div>
                
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.25rem' }}>
                  {sets.map((set, index) => (
                    <div key={index} style={{
                      backgroundColor: 'white',
                    padding: '0.25rem',
                    borderRadius: '0.375rem',
                      border: '1px solid #D97706'
                    }}>
                      <div style={{ 
                        textAlign: 'center', 
                        fontWeight: 'bold', 
                        color: '#92400E',
                      marginBottom: '0.25rem',
                      fontSize: '0.625rem'
                      }}>
                      Set {index + 1}
                      </div>
                      
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.125rem', marginBottom: '0.125rem' }}>
                      <span style={{ fontSize: '0.5rem', color: '#6B7280', minWidth: '30px' }}>
                          {match?.homeTeam}
                        </span>
                        <input
                          type="number"
                          min="0"
                          max={index === 2 ? "20" : "10"}
                          value={set.homeGames || 0}
                          onChange={(e) => updateSetScoreAdmin(index, parseInt(e.target.value) || 0, set.awayGames || 0)}
                          style={{
                          width: '30px',
                          padding: '0.125rem',
                            border: '1px solid #D97706',
                            borderRadius: '0.25rem',
                            textAlign: 'center',
                          fontSize: '0.625rem',
                            fontWeight: 'bold'
                          }}
                        />
                      </div>
                      
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.125rem' }}>
                      <span style={{ fontSize: '0.5rem', color: '#6B7280', minWidth: '30px' }}>
                          {match?.awayTeam}
                        </span>
                        <input
                          type="number"
                          min="0"
                          max={index === 2 ? "20" : "10"}
                          value={set.awayGames || 0}
                          onChange={(e) => updateSetScoreAdmin(index, set.homeGames || 0, parseInt(e.target.value) || 0)}
                          style={{
                          width: '30px',
                          padding: '0.125rem',
                            border: '1px solid #D97706',
                            borderRadius: '0.25rem',
                            textAlign: 'center',
                          fontSize: '0.625rem',
                            fontWeight: 'bold'
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

          {/* Current Game Score */}
          {!currentSetData?.completed && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
                style={{
                textAlign: 'center', 
                padding: '0.5rem',
                background: currentSet === 3 ? 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)' : 'linear-gradient(135deg, #F0FDF4 0%, #D1FAE5 100%)',
                      borderRadius: '0.5rem',
                border: currentSet === 3 ? '2px solid #EF4444' : '2px solid #10B981'
              }}
            >
              <div style={{ fontSize: '0.625rem', fontWeight: 'bold', color: currentSet === 3 ? '#991B1B' : '#065F46', marginBottom: '0.125rem' }}>
                {currentSet === 3 ? 'SUPER TIEBREAK' : 'CURRENT GAME'}
                </div>
              <div style={{ fontSize: '1rem', fontWeight: 'bold', color: currentSet === 3 ? '#DC2626' : '#1C563D' }}>
                {currentSet === 3 
                  ? `${currentSetData?.homeGames || 0} - ${currentSetData?.awayGames || 0}`
                  : getGameScore(currentSetData?.homePoints || 0, currentSetData?.awayPoints || 0)
                }
                </div>
              </motion.div>
            )}

          {/* Game Point Controls */}
            {matchStatus !== 'completed' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: '0.25rem'
              }}
            >
                {/* Home Team Point Controls */}
                <div style={{
                  background: 'linear-gradient(135deg, #F0FDF4 0%, #D1FAE5 100%)',
                borderRadius: '0.5rem',
                padding: '0.5rem',
                  border: '2px solid #10B981'
                }}>
                <div style={{ 
                  fontSize: '0.625rem', 
                    fontWeight: 'bold', 
                    color: '#065F46', 
                  marginBottom: '0.25rem', 
                    textAlign: 'center' 
                  }}>
                  {match?.homeTeam} {currentSet === 3 ? 'Points' : 'Points'}
                </div>
                <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => updateTennisScore('home', -1)}
                      style={{
                        background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                        color: 'white',
                        border: 'none',
                      borderRadius: '0.375rem',
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.625rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                      gap: '0.125rem'
                      }}
                    >
                    <Minus size={10} />
                    -
                    </motion.button>
                    
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => updateTennisScore('home', 1)}
                      style={{
                        background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                        color: 'white',
                        border: 'none',
                      borderRadius: '0.375rem',
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.625rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                      gap: '0.125rem'
                      }}
                    >
                    <Plus size={10} />
                    +
                    </motion.button>
                  </div>
                </div>

                {/* Away Team Point Controls */}
                <div style={{
                  background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
                borderRadius: '0.5rem',
                padding: '0.5rem',
                  border: '2px solid #3B82F6'
                }}>
                <div style={{ 
                  fontSize: '0.625rem', 
                    fontWeight: 'bold', 
                    color: '#1E40AF', 
                  marginBottom: '0.25rem', 
                    textAlign: 'center' 
                  }}>
                  {match?.awayTeam} {currentSet === 3 ? 'Points' : 'Points'}
                </div>
                <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => updateTennisScore('away', -1)}
                      style={{
                        background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                        color: 'white',
                        border: 'none',
                      borderRadius: '0.375rem',
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.625rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                      gap: '0.125rem'
                      }}
                    >
                    <Minus size={10} />
                    -
                    </motion.button>
                    
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => updateTennisScore('away', 1)}
                      style={{
                        background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                        color: 'white',
                        border: 'none',
                      borderRadius: '0.375rem',
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.625rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                      gap: '0.125rem'
                      }}
                    >
                    <Plus size={10} />
                    +
                    </motion.button>
                  </div>
                </div>
            </motion.div>
            )}

          {/* Set Selector */}
            {matchStatus !== 'completed' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              style={{
                background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
                borderRadius: '0.5rem',
                padding: '0.5rem',
                border: '2px solid #F59E0B'
              }}
            >
              <div style={{ 
                fontSize: '0.625rem', 
                fontWeight: '700', 
                color: '#92400E', 
                marginBottom: '0.25rem',
                textAlign: 'center'
              }}>
                Set Navigation
              </div>
              <div style={{ 
                display: 'flex', 
                gap: '0.25rem', 
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}>
                {sets.map((set, index) => (
                  <button
                    key={index}
                    onClick={async () => {
                      console.log(`🎾 Manually selecting Set ${index + 1}`);
                      setIsManuallySettingCurrentSet(true);
                      setCurrentSet(index + 1);
                      // Update the match data to reflect the current set change
                      console.log(`💾 Saving match data with currentSet: ${index + 1}`);
                      await saveMatchData({}, index + 1);
                      console.log(`✅ Match data saved successfully`);
                      // Reset the flag after a short delay
                      setTimeout(() => {
                        setIsManuallySettingCurrentSet(false);
                        console.log(`🔄 Manual set selection flag reset`);
                      }, 1000);
                    }}
                    style={{
                      padding: '0.25rem 0.5rem',
                      background: currentSet === index + 1 ? 'linear-gradient(135deg, #1C563D 0%, #2D7A5A 100%)' : '#E5E7EB',
                      color: currentSet === index + 1 ? 'white' : '#6B7280',
                      border: set.completed ? '2px solid #10B981' : currentSet === index + 1 ? '2px solid #1C563D' : '2px solid transparent',
                      borderRadius: '0.375rem',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.625rem',
                      transition: 'all 0.2s',
                      transform: currentSet === index + 1 ? 'scale(1.05)' : 'scale(1)',
                      boxShadow: currentSet === index + 1 ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'
                    }}
                  >
                    Set {index + 1} {set.completed ? '✓' : currentSet === index + 1 ? '▶' : ''}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Game Controls */}
          {matchStatus !== 'completed' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: '0.25rem'
              }}
            >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    const setIndex = currentSet - 1;
                    const currentSetData = sets[setIndex];
                  updateSetScore(setIndex, (currentSetData.homeGames || 0) + 1, currentSetData.awayGames || 0);
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                    color: 'white',
                    border: 'none',
                  borderRadius: '0.375rem',
                  padding: '0.25rem',
                  fontSize: '0.625rem',
                    fontWeight: 'bold',
                  cursor: 'pointer'
                  }}
                >
                  + {match?.homeTeam} Game
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    const setIndex = currentSet - 1;
                    const currentSetData = sets[setIndex];
                  updateSetScore(setIndex, currentSetData.homeGames || 0, (currentSetData.awayGames || 0) + 1);
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                    color: 'white',
                    border: 'none',
                  borderRadius: '0.375rem',
                  padding: '0.25rem',
                  fontSize: '0.625rem',
                    fontWeight: 'bold',
                  cursor: 'pointer'
                  }}
                >
                  + {match?.awayTeam} Game
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    const setIndex = currentSet - 1;
                    const currentSetData = sets[setIndex];
                  updateSetScore(setIndex, Math.max(0, (currentSetData.homeGames || 0) - 1), currentSetData.awayGames || 0);
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                    color: 'white',
                    border: 'none',
                  borderRadius: '0.375rem',
                  padding: '0.25rem',
                  fontSize: '0.625rem',
                    fontWeight: 'bold',
                  cursor: 'pointer'
                  }}
                >
                  - {match?.homeTeam} Game
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    const setIndex = currentSet - 1;
                    const currentSetData = sets[setIndex];
                  updateSetScore(setIndex, currentSetData.homeGames || 0, Math.max(0, (currentSetData.awayGames || 0) - 1));
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                    color: 'white',
                    border: 'none',
                  borderRadius: '0.375rem',
                  padding: '0.25rem',
                  fontSize: '0.625rem',
                    fontWeight: 'bold',
                  cursor: 'pointer'
                  }}
                >
                  - {match?.awayTeam} Game
                </motion.button>
            </motion.div>
          )}

          {/* Match Completion Button */}
          {(matchStatus === 'live' || matchStatus === 'paused') && (() => {
            const allSetsComplete = sets.every(s => s.completed);
            return (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center', 
                  gap: '0.25rem'
                }}
              >
                {!allSetsComplete && (
                  <div style={{
                    background: 'rgba(251, 191, 36, 0.2)',
                    border: '2px solid #F59E0B',
                    borderRadius: '0.375rem',
                    padding: '0.25rem 0.5rem',
                    color: '#92400E',
                    fontWeight: '600',
                    fontSize: '0.625rem',
                    textAlign: 'center'
                  }}>
                    ⚠️ All 3 sets must be completed
              </div>
            )}
                <motion.button
                  whileHover={{ scale: allSetsComplete ? 1.05 : 1 }}
                  whileTap={{ scale: allSetsComplete ? 0.95 : 1 }}
                  onClick={allSetsComplete ? endMatch : null}
                  disabled={!allSetsComplete}
                  style={{
                    background: allSetsComplete 
                      ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                      : 'linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    padding: '0.5rem 1rem',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    cursor: allSetsComplete ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    opacity: allSetsComplete ? 1 : 0.6
                  }}
                >
                  <Trophy size={14} />
                  Complete Match {allSetsComplete ? '✓' : `(${sets.filter(s => s.completed).length}/3)`}
                </motion.button>
          </motion.div>
            );
          })()}

          {/* Force Complete Button for Admins */}
          {matchStatus !== 'completed' && !isScoreKeeper() && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              style={{
                display: 'flex', 
                justifyContent: 'center'
              }}
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={endMatch}
                    style={{
                  background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.625rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.125rem'
                }}
              >
                <CheckCircle size={12} />
                Force Complete (Admin)
              </motion.button>
            </motion.div>
                    )}
                  </div>

        {/* RIGHT COLUMN - Player Statistics */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', overflow: 'hidden' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
              style={{
              background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)',
              borderRadius: '0.5rem',
              padding: '0.5rem',
              border: '2px solid #E2E8F0',
              height: '100%',
              overflow: 'auto'
            }}
          >
            <div style={{ 
              fontSize: '0.75rem', 
              fontWeight: 'bold', 
              color: '#374151', 
              marginBottom: '0.5rem', 
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.25rem'
            }}>
                Player Statistics
                {savingPlayerStats && (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    style={{ fontSize: '0.5rem' }}
                  >
                    💾
                  </motion.div>
                )}
                {lastUpdateTime && (
                  <div style={{ fontSize: '0.5rem', color: '#10B981' }}>
                    🔄 {lastUpdateTime}
                  </div>
                )}
            </div>

              <div style={{ 
                display: 'grid', 
              gridTemplateColumns: 'repeat(2, 1fr)', 
              gap: '0.5rem' 
              }}>
                {/* Home Team Players */}
                <div>
                <div style={{ 
                  fontSize: '0.625rem', 
                  fontWeight: '700', 
                  color: '#10B981', 
                  marginBottom: '0.25rem', 
                  textAlign: 'center' 
                }}>
                    {match?.homeTeam}
                </div>
                  {getPlayersInOrder(playerStats.home).map(({ playerKey, stats }) => (
                  <div key={playerKey} style={{ 
                    marginBottom: '0.5rem', 
                    padding: '0.375rem', 
                    background: 'linear-gradient(135deg, #F0FDF4 0%, #D1FAE5 100%)', 
                    borderRadius: '0.375rem' 
                  }}>
                    <div style={{ 
                      fontWeight: '700', 
                      marginBottom: '0.25rem', 
                      color: '#065F46', 
                      textAlign: 'center', 
                      fontSize: '0.625rem' 
                    }}>
                        {stats.name || `Player ${playerKey.slice(-1)}`}
                      </div>
                      
                      {/* Aces */}
                    <div style={{ marginBottom: '0.25rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.125rem' }}>
                        <span style={{ fontSize: '0.5rem', fontWeight: '600', color: '#374151' }}>Aces</span>
                        <div style={{ display: 'flex', gap: '0.125rem', alignItems: 'center' }}>
                            <button
                              onClick={() => updatePlayerStat('home', playerKey, 'aces', -1)}
                              style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '0.25rem',
                                background: '#EF4444',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer',
                              fontSize: '0.625rem',
                                fontWeight: 'bold'
                              }}
                            >
                              -
                            </button>
                          <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#1C563D', minWidth: '20px', textAlign: 'center' }}>
                              {stats.aces || 0}
                            </span>
                            <button
                              onClick={() => updatePlayerStat('home', playerKey, 'aces', 1)}
                              style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '0.25rem',
                                background: '#10B981',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer',
                              fontSize: '0.625rem',
                                fontWeight: 'bold'
                              }}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>

                    {/* Smashes */}
                    <div style={{ marginBottom: '0.25rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.125rem' }}>
                        <span style={{ fontSize: '0.5rem', fontWeight: '600', color: '#374151' }}>Smashes</span>
                        <div style={{ display: 'flex', gap: '0.125rem', alignItems: 'center' }}>
                            <button
                              onClick={() => updatePlayerStat('home', playerKey, 'kickSmashes', -1)}
                              style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '0.25rem',
                                background: '#EF4444',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer',
                              fontSize: '0.625rem',
                                fontWeight: 'bold'
                              }}
                            >
                              -
                            </button>
                          <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#1C563D', minWidth: '20px', textAlign: 'center' }}>
                              {stats.kickSmashes || 0}
                            </span>
                            <button
                              onClick={() => updatePlayerStat('home', playerKey, 'kickSmashes', 1)}
                              style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '0.25rem',
                                background: '#10B981',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer',
                              fontSize: '0.625rem',
                                fontWeight: 'bold'
                              }}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Unforced Errors */}
                    <div style={{ marginBottom: '0.25rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.125rem' }}>
                        <span style={{ fontSize: '0.5rem', fontWeight: '600', color: '#374151' }}>Errors</span>
                        <div style={{ display: 'flex', gap: '0.125rem', alignItems: 'center' }}>
                            <button
                              onClick={() => updatePlayerStat('home', playerKey, 'unforcedErrors', -1)}
                              style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '0.25rem',
                                background: '#EF4444',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer',
                              fontSize: '0.625rem',
                                fontWeight: 'bold'
                              }}
                            >
                              -
                            </button>
                          <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#DC2626', minWidth: '20px', textAlign: 'center' }}>
                              {stats.unforcedErrors || 0}
                            </span>
                            <button
                              onClick={() => updatePlayerStat('home', playerKey, 'unforcedErrors', 1)}
                              style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '0.25rem',
                                background: '#10B981',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer',
                              fontSize: '0.625rem',
                                fontWeight: 'bold'
                              }}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>

                    {/* Sportsmanship Rating */}
                      <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.125rem' }}>
                        <span style={{ fontSize: '0.5rem', fontWeight: '600', color: '#374151' }}>Sports</span>
                        <span style={{ fontSize: '0.625rem', fontWeight: 'bold', color: '#F59E0B' }}>
                            {stats.sportsmanshipRating || 5}/5
                          </span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="5"
                          step="1"
                          value={stats.sportsmanshipRating || 5}
                          onChange={(e) => updatePlayerStat('home', playerKey, 'sportsmanshipRating', parseInt(e.target.value) - (stats.sportsmanshipRating || 5))}
                          style={{
                            width: '100%',
                          height: '3px',
                          borderRadius: '2px',
                            background: '#E5E7EB',
                            cursor: 'pointer'
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Away Team Players */}
                <div>
                <div style={{ 
                  fontSize: '0.625rem', 
                  fontWeight: '700', 
                  color: '#3B82F6', 
                  marginBottom: '0.25rem', 
                  textAlign: 'center' 
                }}>
                    {match?.awayTeam}
                </div>
                  {getPlayersInOrder(playerStats.away).map(({ playerKey, stats }) => (
                  <div key={playerKey} style={{ 
                    marginBottom: '0.5rem', 
                    padding: '0.375rem', 
                    background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)', 
                    borderRadius: '0.375rem' 
                  }}>
                    <div style={{ 
                      fontWeight: '700', 
                      marginBottom: '0.25rem', 
                      color: '#1E40AF', 
                      textAlign: 'center', 
                      fontSize: '0.625rem' 
                    }}>
                        {stats.name || `Player ${playerKey.slice(-1)}`}
                      </div>
                      
                      {/* Aces */}
                    <div style={{ marginBottom: '0.25rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.125rem' }}>
                        <span style={{ fontSize: '0.5rem', fontWeight: '600', color: '#374151' }}>Aces</span>
                        <div style={{ display: 'flex', gap: '0.125rem', alignItems: 'center' }}>
                            <button
                              onClick={() => updatePlayerStat('away', playerKey, 'aces', -1)}
                              style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '0.25rem',
                                background: '#EF4444',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer',
                              fontSize: '0.625rem',
                                fontWeight: 'bold'
                              }}
                            >
                              -
                            </button>
                          <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#1C563D', minWidth: '20px', textAlign: 'center' }}>
                              {stats.aces || 0}
                            </span>
                            <button
                              onClick={() => updatePlayerStat('away', playerKey, 'aces', 1)}
                              style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '0.25rem',
                                background: '#3B82F6',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer',
                              fontSize: '0.625rem',
                                fontWeight: 'bold'
                              }}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>

                    {/* Smashes */}
                    <div style={{ marginBottom: '0.25rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.125rem' }}>
                        <span style={{ fontSize: '0.5rem', fontWeight: '600', color: '#374151' }}>Smashes</span>
                        <div style={{ display: 'flex', gap: '0.125rem', alignItems: 'center' }}>
                            <button
                              onClick={() => updatePlayerStat('away', playerKey, 'kickSmashes', -1)}
                              style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '0.25rem',
                                background: '#EF4444',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer',
                              fontSize: '0.625rem',
                                fontWeight: 'bold'
                              }}
                            >
                              -
                            </button>
                          <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#1C563D', minWidth: '20px', textAlign: 'center' }}>
                              {stats.kickSmashes || 0}
                            </span>
                            <button
                              onClick={() => updatePlayerStat('away', playerKey, 'kickSmashes', 1)}
                              style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '0.25rem',
                                background: '#3B82F6',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer',
                              fontSize: '0.625rem',
                                fontWeight: 'bold'
                              }}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Unforced Errors */}
                    <div style={{ marginBottom: '0.25rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.125rem' }}>
                        <span style={{ fontSize: '0.5rem', fontWeight: '600', color: '#374151' }}>Errors</span>
                        <div style={{ display: 'flex', gap: '0.125rem', alignItems: 'center' }}>
                            <button
                              onClick={() => updatePlayerStat('away', playerKey, 'unforcedErrors', -1)}
                              style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '0.25rem',
                                background: '#EF4444',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer',
                              fontSize: '0.625rem',
                                fontWeight: 'bold'
                              }}
                            >
                              -
                            </button>
                          <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#DC2626', minWidth: '20px', textAlign: 'center' }}>
                              {stats.unforcedErrors || 0}
                            </span>
                            <button
                              onClick={() => updatePlayerStat('away', playerKey, 'unforcedErrors', 1)}
                              style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '0.25rem',
                                background: '#3B82F6',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer',
                              fontSize: '0.625rem',
                                fontWeight: 'bold'
                              }}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Sportsmanship Rating */}
                      <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.125rem' }}>
                        <span style={{ fontSize: '0.5rem', fontWeight: '600', color: '#374151' }}>Sports</span>
                        <span style={{ fontSize: '0.625rem', fontWeight: 'bold', color: '#F59E0B' }}>
                            {stats.sportsmanshipRating || 5}/5
                          </span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="5"
                          step="1"
                          value={stats.sportsmanshipRating || 5}
                          onChange={(e) => updatePlayerStat('away', playerKey, 'sportsmanshipRating', parseInt(e.target.value) - (stats.sportsmanshipRating || 5))}
                          style={{
                            width: '100%',
                          height: '3px',
                          borderRadius: '2px',
                            background: '#E5E7EB',
                            cursor: 'pointer'
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

      </div>
    </div>
  );
};

export default LiveScoringPage;
